import {
  Col,
  DefineTermDescription,
  Flex,
  Row,
  Text,
  TitledText,
  theme,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  type PinpointSheetType,
  getPinpointSceneAndFieldBySheetType,
} from '@/features/comment/utils/pinpoint';
import { useMinistries } from '@/features/master-table/hooks';
import { getMinistryName } from '@/features/mold/utils';
import { useBusinessManagers } from '@/features/project/_hooks/business-managers';
import { useProjectHasRelatedOrganizations } from '@/features/project/_hooks/organization';
import type { FundAdditionalAttributesModel } from '@/features/project/projects/_models/project';
import { useFilteredUsers } from '@/features/tenant-manager/hooks';
import { useId } from '@/hooks';
import { ITEM_SPLITTER, findUpperRecursive, isPS } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { ProjectNumberPinpointItem } from './pinpoint-items/basic-information-pinpoint-fields';

type Props = {
  ministryId: string;
  organizationId: string;
  relatedOrganizationIds: string[];
  otherResponsibleOrganizationIds: string[];
  peopleInChargeIds: string[] | null;
  businessManagerIds: string[];
  additionalJurisdictionalUsers?: z.infer<
    typeof FundAdditionalAttributesModel.shape.additionalJurisdictionalUsers
  >;
  historySavedAt: string | undefined;
  pinpointSheetType: PinpointSheetType;
  projectNumber?: string | null;
};

export const ProjectInformationHeader: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useId();

  const projectOrganizations = useProjectHasRelatedOrganizations({
    projectId: id,
    params: { showMigrated: true, historySavedAt: props.historySavedAt },
    enabled: !!id,
  });

  const nestedOrganizations = useMemo(() => {
    if (!projectOrganizations.data?.length) {
      return undefined;
    }
    return findUpperRecursive(
      props.organizationId,
      projectOrganizations.data,
      [],
    )
      .reverse()
      .map((d) => d.name)
      .join(ITEM_SPLITTER);
  }, [projectOrganizations.data, props.organizationId]);

  const _nestedRelatedOrganizationNames = useMemo(() => {
    if (
      !projectOrganizations.data?.length ||
      !props.relatedOrganizationIds.length
    ) {
      return [];
    }
    return props.relatedOrganizationIds.map((id) =>
      findUpperRecursive(id, projectOrganizations.data, [])
        .reverse()
        .map((d) => d.name)
        .join(ITEM_SPLITTER),
    );
  }, [projectOrganizations.data, props.relatedOrganizationIds]);

  const _nestedOtherResponsibleOrganizationNames = useMemo(() => {
    if (
      !projectOrganizations.data?.length ||
      !props.otherResponsibleOrganizationIds.length
    ) {
      return [];
    }
    return props.otherResponsibleOrganizationIds.map((id) =>
      findUpperRecursive(id, projectOrganizations.data, [])
        .reverse()
        .map((d) => d.name)
        .join(ITEM_SPLITTER),
    );
  }, [projectOrganizations.data, props.otherResponsibleOrganizationIds]);

  const ministries = useMinistries();

  const peopleInChargesData = useFilteredUsers({
    params: {
      filterBy: 'ids',
      ids: !isPS() ? (props.peopleInChargeIds ?? []) : [],
      showMigrated: true,
      historySavedAt: props.historySavedAt,
      showDeleted: true,
    },
    select: (data) =>
      data.results.map((person) => {
        if (isPS()) {
          return person.name;
        }

        return [
          person.name,
          ...(person.organizations?.map((v) => v.name) ?? []),
        ]
          .filter(Boolean)
          .join(ITEM_SPLITTER);
      }),
    enabled: !!props.peopleInChargeIds?.length,
  });

  const businessManagerNames = useBusinessManagers({
    projectId: id,
    select: (data) => {
      return data.map((person) => {
        if (isPS()) {
          return person.name;
        }
        return [
          person.name,
          ...(person.organizations?.map((v) => v.name) ?? []),
        ]
          .filter(Boolean)
          .join(ITEM_SPLITTER);
      });
    },
  });

  return (
    <Flex
      vertical
      style={{ backgroundColor: token.colorBgLayout, padding: 16 }}
      gap={12}
    >
      <ProjectNumberPinpointItem
        key="project-number"
        projectNumber={props.projectNumber ?? null}
        pinpointSheetType="RS"
        projectId={id}
      />
      <Row gutter={[24, 12]}>
        <Col span={12}>
          <TitledText
            variant="text"
            title={t('pelaksana-program')}
            text={nestedOrganizations ?? ''}
          />
        </Col>
        <Col span={12}>
          <TitledText
            variant="text"
            title={t('ministry')}
            text={getMinistryName({
              targetId: props.ministryId,
              ministries: ministries.data,
            })}
          />
        </Col>
      </Row>
      <Row gutter={[24, 12]}>
        <Col span={12}>
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType: props.pinpointSheetType,
              rs: {
                path: { project: id },
                field: 'rs.people-in-charge',
              },
              rsSkippedReview: {
                field: 'rs.skippedReview.people-in-charge',
                path: { project: id },
              },
              ss: {
                path: { project: id },
                field: 'ss.people-in-charge',
              },
              ksInvestment: {
                path: { project: id },
                field: 'ks.investment.people-in-charge',
              },
              ksStandard: {
                path: { project: id },
                field: 'ks.standard.people-in-charge',
              },
              ksLocalGovernment: {
                path: { project: id },
                field: 'ks.local-government.people-in-charge',
              },
            })}
          >
            <DefineTermDescription
              term={t('people-in-charge')}
              items={
                peopleInChargesData.data?.map((item, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                  <Text key={index} variant="footnote" text={item} />
                )) ?? []
              }
              countValues
              thresholdToViewMore={2}
            />
          </PinpointMarker>
        </Col>
        <Col span={12}>
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType: props.pinpointSheetType,
              rs: {
                path: { project: id },
                field: 'rs.business-manager',
              },
              rsSkippedReview: {
                field: 'rs.skippedReview.business-manager',
                path: { project: id },
              },
              ss: {
                path: { project: id },
                field: 'ss.business-manager',
              },
              ksInvestment: {
                path: { project: id },
                field: 'ks.investment.business-manager',
              },
              ksStandard: {
                path: { project: id },
                field: 'ks.standard.business-manager',
              },
              ksLocalGovernment: {
                path: { project: id },
                field: 'ks.local-government.business-manager',
              },
            })}
          >
            <DefineTermDescription
              term={t('business-manager')}
              items={
                businessManagerNames.data?.map((item, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                  <Text key={index} variant="footnote" text={item} />
                )) ?? []
              }
              countValues
              thresholdToViewMore={2}
            />
          </PinpointMarker>
        </Col>
      </Row>
    </Flex>
  );
};
